import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import type { RouterHistory } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Toaster, TooltipProvider } from "@erp/ui";

import { AppLayout } from "@/app-shell/app-layout";
import { ForgotPasswordPage } from "@/features/auth/forgot-password-page";
import { LoginPage } from "@/features/auth/login-page";
import { ResetPasswordPage } from "@/features/auth/reset-password-page";
import { catalogRoutes } from "@/features/catalog/routes";
import { DashboardRoute } from "@/features/dashboard/routes";
import { identityRoutes } from "@/features/identity/routes";
import { ledgerRoutes } from "@/features/ledger/routes";
import { financeRoutes } from "@/features/finance/routes";
import { inventoryRoutes } from "@/features/inventory/routes";
import { partnersRoutes } from "@/features/partners/routes";
import { purchasingRoutes } from "@/features/purchasing/routes";
import { salesRoutes } from "@/features/sales/routes";
import { channelsRoutes } from "@/features/channels/routes";
import { appsRoutes } from "@/features/apps/routes";
import { platformRoutes } from "@/features/platform/routes";

export interface RouterContext {
  queryClient: QueryClient;
}

function RootLayout() {
  const { t } = useTranslation();
  return (
    <TooltipProvider>
      <Outlet />
      <Toaster closeLabel={t("actions.close")} />
    </TooltipProvider>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

export interface LoginSearch {
  /** Where to land after a successful sign-in (href captured by the guard). */
  redirect?: string;
  /** "expired" renders the session-expired banner. */
  reason?: "expired";
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  // Absent keys must be OMITTED (not set to undefined): explicit undefined
  // defeats the router's same-location dedupe and can re-fire redirects.
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const validated: LoginSearch = {};
    if (typeof search.redirect === "string") validated.redirect = search.redirect;
    if (search.reason === "expired") validated.reason = "expired";
    return validated;
  },
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

export interface ResetPasswordSearch {
  token?: string;
}

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => {
    const validated: ResetPasswordSearch = {};
    if (typeof search.token === "string") validated.token = search.token;
    return validated;
  },
  component: ResetPasswordPage,
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  component: AppLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  component: DashboardRoute,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authedRoute.addChildren([
    dashboardRoute,
    ...catalogRoutes(authedRoute),
    ...ledgerRoutes(authedRoute),
    ...identityRoutes(authedRoute),
    ...financeRoutes(authedRoute),
    ...inventoryRoutes(authedRoute),
    ...partnersRoutes(authedRoute),
    ...purchasingRoutes(authedRoute),
    ...salesRoutes(authedRoute),
    ...channelsRoutes(authedRoute),
    ...platformRoutes(authedRoute),
    ...appsRoutes(authedRoute),
  ]),
]);

export interface CreateAppRouterOptions {
  /** Override the history implementation (memory history in tests). */
  history?: RouterHistory;
}

export function createAppRouter(queryClient: QueryClient, options: CreateAppRouterOptions = {}) {
  return createRouter({
    routeTree,
    context: { queryClient },
    history: options.history,
    defaultPreload: false,
    scrollRestoration: true,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
